import type { Score } from '../../music/model';
import { measureCount } from '../../music/model';
import { handsOverlap, isMonophonic } from '../../setup/diagnostics';
import { useSettings } from '../hooks/stores';
import type { BestResult, HandsKey, ScoreProgress } from '../../store/progress';
import { formatDuration, handLabel, levelLabel, percent, scoreDurationMs } from '../format';
import { href } from '../router';

/** Meilleur score global enregistré, toutes mains confondues. */
export function bestQuality(progress: ScoreProgress | undefined): BestResult | undefined {
  if (!progress) return undefined;
  let best: BestResult | undefined;
  for (const hp of Object.values(progress.hands)) {
    for (const r of [hp?.bestTempo, hp?.bestWait]) {
      if (r && (!best || r.quality > best.quality)) best = r;
    }
  }
  return best;
}

export function StatusBadge({ progress }: { progress?: ScoreProgress }) {
  if (!progress || progress.status === 'new') return <span className="badge new">Nouveau</span>;
  if (progress.status === 'passed') return <span className="badge passed">Réussi</span>;
  const best = bestQuality(progress);
  return <span className="badge started">Commencé{best ? ` · ${percent(best.quality)}` : ''}</span>;
}

const HAND_KEYS: [HandsKey, string][] = [
  ['right', 'MD'],
  ['left', 'MG'],
  ['both', 'MD + MG'],
];

/** Pour un morceau à deux mains : état de chaque combinaison de mains travaillée. */
export function HandsChips({ score, progress }: { score: Score; progress?: ScoreProgress }) {
  if (score.parts.length < 2 || !progress || progress.status === 'new') return null;
  return (
    <div className="hands-chips" aria-label="Progression par mains">
      {HAND_KEYS.map(([key, label]) => {
        const status = progress.hands[key]?.status ?? 'new';
        const mark = status === 'passed' ? '✓' : status === 'started' ? '…' : '○';
        const title = status === 'passed' ? 'réussi' : status === 'started' ? 'commencé' : 'pas encore travaillé';
        return (
          <span key={key} className={`chip ${status}`} title={`${label} : ${title}`}>
            {label} {mark}
          </span>
        );
      })}
      {progress.hands.unknown && (
        <span className="chip legacy" title="Résultats de la version 1 : mains et entrée non enregistrées">
          V1
        </span>
      )}
    </div>
  );
}

export function ScoreCard({ score, progress }: { score: Score; progress?: ScoreProgress }) {
  const settings = useSettings();
  const hands = score.parts.map((p) => p.hand);
  // Même règle que l'assistant et l'écran de pratique : au micro, une main à la fois quand les deux jouent ensemble.
  const micOneHand = isMonophonic(settings.inputKind) && handsOverlap(score);
  return (
    <a className="card score-card" href={href('pratique', score.id)}>
      <div className="card-head">
        <span className="level" title={levelLabel(score.level)} aria-label={`Niveau ${score.level} sur 5, ${levelLabel(score.level)}`}>
          {'●'.repeat(score.level)}
          {'○'.repeat(5 - score.level)}
        </span>
        <StatusBadge progress={progress} />
      </div>
      <h3>{score.title}</h3>
      {score.subtitle && <p className="subtitle">{score.subtitle}</p>}
      <p className="desc">{score.description}</p>
      <HandsChips score={score} progress={progress} />
      {micOneHand && <span className="badge mic">Au micro : une main à la fois</span>}
      <div className="meta-row">
        <span>{hands.map((h) => handLabel(h, true)).join(' + ')}</span>
        <span>
          {measureCount(score)} mesures · {formatDuration(scoreDurationMs(score))}
        </span>
        <span>♩ = {score.tempo}</span>
      </div>
    </a>
  );
}
